import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {Box, Step, Stepper, StepButton, useTheme, Paper, AppBar, Stack, Typography} from "@mui/material";
import {useRecoilState, useSetRecoilState} from "recoil";
import {useMutation, useQuery} from "@tanstack/react-query";
import Chip from "@mui/material/Chip";
import {HealthAndSafety} from "@mui/icons-material";
import {UseQueryResult} from "@tanstack/react-query/src/types";
import {AxiosError} from "axios";
import _ from "underscore";
import {CREATE_RA_STEPS, RECOVERY_CREATE_RA_STEPS, CREATE_RA_STEPS_KEYLESS, RECOVERY_CREATE_RA_STEPS_KEYLESS} from "../config";
import {
    bookingDataValueState,
    counterSaleManagementValueState,
    createRaPricingInfoErrorState,
    createRaPricingInfoState,
    customerDataValueState,
    driverResidenceCountriesListState,
    preauthState,
    prefixesListState,
    recoveryRaInfoState,
    recoveryRaModeState,
    rentalAgreementFillingValueState,
    stepState,
    verifySignState
} from "./state";
import ContentBody from "../common/content-body/ContentBody";
import CustomerData from "./customer-data/CustomerData";
import CheckDriversValidity from "./check-drivers-validity/CheckDriversValidity";
import OtpValidation from "./otp-validation/OtpValidation";
import RaFilling from "./ra-filling/RaFilling";
import PreviewAndSigning from "./preview-and-signing/PreviewAndSigning";
import PreAuth from "./pre-auth/PreAuth";
import SectionHeader from "../../../util/component/SectionHeader";
import {
    findCounterSaleManagement, fetchCountries, fetchCreateRaPricingAncillaryInfo,
    fetchPrefixes, retriveDraftRA, verifySignRa, checkTransactionRa,
    raUpdateByCounterSale, fetchBusinessContinuitySkipPayment, raPreAuthPaymentInvoiceDraft
} from "./services/http.services";
import {fillPersonResponse} from "../utils";
import {snackbarTypes} from "../../../util/state/globalVariables";
import {setSnackbar} from "../../../util/state/globalAction";
import CustomLoader from "../../../util/component/CustomLoader";
import {GlobalContext} from "../../../util/state/GlobalContextProvider";
import {AncillaryTotalCostInfo} from "./ra-filling/ancillaries-info/AncillariesInfo";
import {ICounterSale} from "../models/models";
import RecoveryDialogReason from "./recovery-dialog-reason/RecoveryDialogReason";
import ConsentMgmt from "./consent-mgmt/ConsentMgmt";
import dayjs from "dayjs";
import NotAllowedDialog from "../../authentication/component/NotAllowedDialog";
import notAllowedDialog from "../../authentication/component/NotAllowedDialog";
import { generalInfoState } from "../search-dms/state";
import { getMobilityStoreBySapId } from "../search-dms/dmsSearch.actions";

/**
 * Create RA Component
 */
const CreateRa = () => {
    const theme = useTheme();
    const {t} = useTranslation(['network', 'translation']);
    const navigate = useNavigate();
    const { dispatchGlobalData } = useContext(GlobalContext);
    const params = useParams(); // params.id
    const [queryParameters] = useSearchParams();
    // Use Recoil
    const [step, _setStep] = useRecoilState(stepState);
    const setStep : typeof _setStep = (...args) => {
      console.trace("step updated")
      _setStep(...args)
    }
    const setRaFilling = useSetRecoilState(rentalAgreementFillingValueState);
    const [bookingDataValue, setBookingDataValue] = useRecoilState(bookingDataValueState);
    // form values state
    const setCustomerDataValue = useSetRecoilState(customerDataValueState);
    const setPrefixesList = useSetRecoilState(prefixesListState);
    const setCountriesList = useSetRecoilState(driverResidenceCountriesListState);
    const setVerifySign = useSetRecoilState(verifySignState);
    const [counterSaleManagement, setCounterSaleManagement] = useRecoilState<{previous: ICounterSale, last: ICounterSale}>(counterSaleManagementValueState);
    const setCreateRaPricingInfo = useSetRecoilState(createRaPricingInfoState);
    const setRecoveryRaInfo = useSetRecoilState(recoveryRaInfoState);
    const [recoveryMode, setRecoveryMode] = useRecoilState(recoveryRaModeState);
    const setCreateRaPricingInfoError = useSetRecoilState(createRaPricingInfoErrorState);
    const [preAuthAmount, setPreAuthAmount] = useRecoilState(preauthState)

    const createRaStepperRef = useRef<any>();
    const createRaContentRef = useRef<any>();

    const [reasonRecoveryDialog, setReasonRecoveryDialog] = useState<boolean>(false);
    const [notAllowedDialogProps, setNotAllowedDialogProps] = useState<any>({open: false, title: '', message: '', buttonLabel: '', handleClose: () => {}});

    const [isRaNotPermitted, setIsRaNotPermitted] = useState<boolean>(false);

    const keyLess = (queryParameters.has('keyless') && queryParameters.get('keyless') === '1');
    const [keyLessInfo, setKeyLessInfo] = useState<any>(null);
    
    //LB
    useEffect(() => {
      setPreAuthAmount({
        firstPreAuth: bookingDataValue?.preautAmountKeyless
      })
    }, [bookingDataValue?.preautAmountKeyless])
    
    useEffect(() => {
      //console.log("LB setPreAuthAmount");
      setPreAuthAmount({
        firstPreAuth: bookingDataValue?.preautAmountKeyless
      })
      //console.log("LB setPreAuthAmount -->" , preAuthAmount);
    }, [bookingDataValue?.preautAmountKeyless])

    useEffect(() => {
      if (keyLess && bookingDataValue?.dmsCode?.code) {
        getMobilityStoreBySapId(bookingDataValue?.dmsCode?.code)
          .then((res:any) => {
            setKeyLessInfo(res?.at(0).keyLessInfo || null);
          })
          .catch((err: AxiosError) => {
            console.error(err);
          });
      }

      if (!keyLess) setKeyLessInfo(null);
    }, [queryParameters, bookingDataValue])

    const steps = useMemo(() => {
      if (keyLess) {
        return recoveryMode ? RECOVERY_CREATE_RA_STEPS_KEYLESS : CREATE_RA_STEPS_KEYLESS;
      }
      return recoveryMode ? RECOVERY_CREATE_RA_STEPS : CREATE_RA_STEPS;
  }, [recoveryMode, keyLess]);

    const handleStep = (step: number) => {};

    const initCreateRaFlow = (response: any = null, shouldSetStep: boolean = true) => {
        let data = !!response ? response : bookingDataValue;
        const addDr = [];
        if (!_.isEmpty(data.firstDriver)) {
          addDr.push(fillPersonResponse(data.firstDriver));
        }
        if (!_.isEmpty(data.driver)) {
          addDr.push(fillPersonResponse(data.driver));
        }
        setCustomerDataValue({
          mainDriver: fillPersonResponse(data.raOwner),
          additionalDrivers: addDr
        });

        fetchCreateRaPricingAncillaryInfoMutation.mutate({
            productCode: data.financialInfo.product,
            pickUpLocation: data.bookingDetail?.pickUpLocation?.code,
            carGroup: data.bookingDetail?.sippCode || data.bookingDetail?.bookedAcriss,
            segment: data.financialInfo.segment,
            creditType: data.financialInfo.creditType,
            categoryCode: data.financialInfo.category,
            paymentTiming: data.financialInfo.paymentTiming
        } as any);

        // setStep(steps.CUSTOMER_DATA);
        if (shouldSetStep) {
          setStep({
            active: 0,
            completed: step.completed,
            numSteps: step.numSteps,
          });
        }
    };

    useEffect(() => {
      if(isRaNotPermitted) {
        viewErrorMessage('customerData.message.error.raNotPermitted');
        setIsRaNotPermitted(false);
      }

    }, [isRaNotPermitted])

    const retriveDraftRaQuery: UseQueryResult = useQuery(['retriveDraftRA', { id: params.id }],
        () => retriveDraftRA({idRA: params.id, recoveryMode: queryParameters.get('recoveryMode') === '1', keyLess: keyLess}), {
        enabled: false,
        onSuccess: async (response: any) => {
            await setBookingDataValue(response);

            if (recoveryMode) {
                if (!response.hasRecoveryNote) {
                    setReasonRecoveryDialog(true);
                }
                else {
                    setRecoveryRaInfo({
                        effectivePickupDate: dayjs(response.recoveryDate),
                        reason: response.notes[0].body // se sto in modalita recovery è sempre in pos 0 (by Marta)
                    })
                }
            } else {

              setIsRaNotPermitted(!!response?.bookingDetail?.dropoffDate && dayjs(response.bookingDetail.dropoffDate).isBefore(dayjs()));
            }

            if (!!response.idRentalAgreement) {
                verifySignRaMutation.mutate(response.idRentalAgreement);
                initCreateRaFlow(response, false);
            } else {
                initCreateRaFlow(response);
            }
        },
        onError: (error: AxiosError) => {
            // @ts-ignore
            if (error.response?.status === 400 && error.response?.data?.errorDetails === "\"ERROR_RA_ONGOING\"") {
                dispatchGlobalData({
                    type: 'SET_SNACKBAR',
                    payload: {
                        snackbar: {
                            message: t('customerData.message.error.raOnGoing'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            // @ts-ignore
            } else if (error.response?.status === 400 && error.response?.data?.errorDetails === "\"ERROR_INCONSISTENT_RA_STATE\"") {
                dispatchGlobalData({
                    type: 'SET_SNACKBAR',
                    payload: {
                        snackbar: {
                            message: t('customerData.message.error.inconsistentRaState'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            // @ts-ignore
            } else if (error.response?.status === 400 && error.response?.data?.errorDetails === "\"ERROR_INVALID_BOOKING_STATUS\"") {
                setNotAllowedDialogProps({
                    open: true,
                    title: t("common.error.operationNotAllowed"),
                    message: t("common.error.invalidBookingStatus"),
                    buttonLabel: t('common.button.viewTheBooking'),
                    handleClose: () => {
                        navigate(`/booking/view/${params.id}`);
                    }
                });
            // @ts-ignore
            } else if (error.response?.status === 500 && error.response?.data?.errorDetails === "\"ERROR_GET_FNN_VAT\"") {
                dispatchGlobalData({
                    type: 'SET_SNACKBAR',
                    payload: {
                        snackbar: {
                            message: t('common.error.fnnVatBaError'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            } else {
                dispatchGlobalData({
                    type: 'SET_SNACKBAR',
                    payload: {
                        snackbar: {
                            message: t('translation:error.tryLater'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            }
        }
    })

    const viewErrorMessage = (message: string, type = snackbarTypes.error) => {
        dispatchGlobalData({
            type: setSnackbar,
            payload: {
                snackbar: {
                    message: t(message),
                    type: type,
                    show: true,
                    autoClose: true,
                }
            }
        });
    }

    const counterSaleManagementMutation = useMutation(
        findCounterSaleManagement,
        {
            onSuccess: (response: ICounterSale) => {
                setCounterSaleManagement({previous: response, last: response});
            },
            onError: async (error: any) => {
                viewErrorMessage('customerData.message.error.tryLater');
            }
        }
    );

  const fetchCreateRaPricingAncillaryInfoMutation = useMutation(
    fetchCreateRaPricingAncillaryInfo,
    {
      onSuccess: async (response: any) => {
        setCreateRaPricingInfo(response);
      },
      onError: (error: AxiosError) => {
        if (error.response?.status === 400) {
          if ((error.response as any)?.data?.errorDetails === "\"ERROR_PRICING_BAD_CONFIGURATION\"") {
            viewErrorMessage("common.error.pricingBadConfiguration");
          } else if ((error.response as any)?.data?.errorDetails === "\"ERROR_PRICING_NOT_SELLABLE\"") {
            viewErrorMessage("common.error.pricingNotSellable", snackbarTypes.warning);
          } else if ((error.response as any)?.data?.errorDetails === "\"ERROR_PRICING_NOT_CONFIGURED\"") {
            viewErrorMessage("common.error.pricingNotConfigured");
          }
        } else {
          viewErrorMessage('translation:error.tryLater');
        }

        setCreateRaPricingInfoError(true);
      }
    });

    const checkTransactionRaMutation = useMutation(
        checkTransactionRa,
    {
        onSuccess: async (response: any) => {

          if( !keyLess && bookingDataValue.hasAnyTransaction && bookingDataValue.preautAmountKeyless ){ 
            initCreateRaFlow();
            return;
          }

          //console.log('LB checkTransactionRaMutation ', response);
          //console.log('LB bookingDataValue ', bookingDataValue);
            if (response.skipToPayment || bookingDataValue.hasAnyTransaction && !bookingDataValue.bookingDetail.flagSubstitution) { // goto the step PreAuth
                const counterSale = await counterSaleManagementMutation.mutateAsync({idRentalAgreement: bookingDataValue.idRentalAgreement});
                setCounterSaleManagement({previous: counterSale, last: counterSale});
              
                setRaFilling({
                    raState:                        bookingDataValue.raState,
                    vehicleDetails: {
                      tenant:                     bookingDataValue.vehicle?.idTenant,
                      acrissCode:                 bookingDataValue.vehicle?.acrissId,
                      acrissLabel:                bookingDataValue.vehicle?.acrissDescription,
                      sippCode:                   bookingDataValue.vehicle?.sippId,
                      sippLabel:                  bookingDataValue.vehicle?.sippDescription,
                      vin:                        bookingDataValue.vehicle?.vin,
                      plate:                      bookingDataValue.vehicle?.plate?.code,
                      enginePowerGroupCode:       bookingDataValue.vehicle?.enginePowerGroupCode,
                      kms:                        bookingDataValue.vehicle?.kilometersOnStart,
                      currentTermicTankFilling:   bookingDataValue.vehicle?.currentTermicTankFilling,
                      currentElectricTankFilling: bookingDataValue.vehicle?.currentElectricTankFilling,
                      rfidCardNumber:             bookingDataValue.vehicle?.currentTermicTankFilling,
                      chargingCableType2:         !!bookingDataValue.vehicle?.chargingCableType2 ? 'YES' : 'NO',
                      chargingCableDomesticType:  !!bookingDataValue.vehicle?.chargingCableDomesticType ? 'YES' : 'NO'
                    },
                    generalInfo: {
                      billingCarGroup: bookingDataValue.vehicle?.acrissId
                    },
                    billingOwnerInfo: {
                      first: {
                        person: {
                          customerId: bookingDataValue.raOwner.customerId
                        }
                      }
                    }
                  });

                // setStep(step.nextStep);
                //let stp = 4;
                //let completed = [true, true, true, true, false, false];
                let stp = 0;
                let completed = [true, false, false, false, false, false];

                if (keyLess) {
                  //console.log('LB keyLess ', keyLess);
                  stp++;
                  completed = [true, true, true, true, false, false];
                  if (recoveryMode) {
                    stp++;
                    completed = [true, true, true, true, false, false];
                  }
                }else {
                    stp = 4;
                  if (recoveryMode) {
                    stp++;
                    completed = [true, true, true, true, true, false, false];
                  }else{
                    completed = [true, true, true, true, false, false];
                  }
                }

                  //console.log('LB setStep ', stp);

                  setStep({
                    active: stp,
                    completed: completed,
                    numSteps: step.numSteps
                  });
                } else {
                    initCreateRaFlow();
                }
            },
            onError: (error: AxiosError) => {
                dispatchGlobalData({
                    type: 'SET_SNACKBAR',
                    payload: {
                        snackbar: {
                            message: t('translation:error.tryLater'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            }
        })

    const verifySignRaMutation = useMutation(
        verifySignRa,
        {
            onSuccess: async (response: any) => {
                //console.log('LBresponse verifySignRaMutation' , response);
                if (response.hasAnyDigitalSignature || response.signed) {
                    setRaFilling({
                    raState:                        bookingDataValue.raState,
                    vehicleDetails: {
                      tenant:                     bookingDataValue.vehicle?.idTenant,
                      acrissCode:                 bookingDataValue.vehicle?.acrissId,
                      acrissLabel:                bookingDataValue.vehicle?.acrissDescription,
                      sippCode:                   bookingDataValue.vehicle?.sippId,
                      sippLabel:                  bookingDataValue.vehicle?.sippDescription,
                      vin:                        bookingDataValue.vehicle?.vin,
                      plate:                      bookingDataValue.vehicle?.plate?.code,
                      enginePowerGroupCode:       bookingDataValue.vehicle?.enginePowerGroupCode,
                      kms:                        bookingDataValue.vehicle?.kilometersOnStart,
                      currentTermicTankFilling:   bookingDataValue.vehicle?.currentTermicTankFilling,
                      currentElectricTankFilling: bookingDataValue.vehicle?.currentElectricTankFilling,
                      rfidCardNumber:             bookingDataValue.vehicle?.currentTermicTankFilling,
                      chargingCableType2:         !!bookingDataValue.vehicle?.chargingCableType2 ? 'YES' : 'NO',
                      chargingCableDomesticType:  !!bookingDataValue.vehicle?.chargingCableDomesticType ? 'YES' : 'NO'
                    },
                    generalInfo: {
                      billingCarGroup: bookingDataValue.vehicle?.acrissId
                    },
                    billingOwnerInfo: {
                      first: {
                        person: {
                          customerId: bookingDataValue.raOwner.customerId
                        }
                      }
                    }
                  });

                    setVerifySign(response);
                    
                    setStep({
                                active: 5,
                                completed: [true, true, true, true, true, false],
                    numSteps: step.numSteps
                  });
                } else {
                    checkTransactionRaMutation.mutate({idRentalAgreement: bookingDataValue.idRentalAgreement, isSubstitution: !!bookingDataValue.bookingDetail?.flagSubstitution} as any);
                }
            },
            onError: (error: AxiosError) => {
                dispatchGlobalData({
                  type: 'SET_SNACKBAR',
                  payload: {
                    snackbar: {
                      message: t('translation:error.tryLater'),
                      type: snackbarTypes.error,
                      show: true,
                      autoClose: true,
                    }
                  }
                });
              }
            }
    );

  /**  Fetch Countries */
  const fetchCountriesQuery = useQuery(['fetchCountries'], () => fetchCountries(), {
    enabled: false,
    onSuccess: (response: any) => {
      setCountriesList(response.map((elem: any) => ({ code: elem, description: elem })));
    },
    onError: (error: AxiosError) => {
      dispatchGlobalData({
        type: setSnackbar,
        payload: {
          snackbar: {
            message: t('customerData.message.error.tryLater'),
            type: snackbarTypes.error,
            show: true,
            autoClose: true,
          }
        }
      });
    }
  })

  /**  Fetch Prefixes */
  const fetchPrefixesQuery = useQuery(['fetchPrefixes'], () => fetchPrefixes(), {
    enabled: false,
    onSuccess: (response: any) => {
      setPrefixesList(response.map((elem: any) => ({ code: elem.country, description: elem.prefix })));
    },
    onError: () => {
      dispatchGlobalData({
        type: setSnackbar,
        payload: {
          snackbar: {
            message: t('customerData.message.error.tryLater'),
            type: snackbarTypes.error,
            show: true,
            autoClose: true,
          }
        }
      });
    }
  })

  // const handleOnScrollEvent = () => {
  //     console.log('onscroll event: ', sticky);
  // }

    const handleRecoveryRaReasonClose = () => {
        setReasonRecoveryDialog(false);
    }

    const handleSaveReasonSave = (data: any) => {
        if (!!data) {
            setRecoveryRaInfo(data);
            handleRecoveryRaReasonClose();
        }
    }

    const checkRecoveryMode = async () => {
        if (queryParameters.get('recoveryMode') === '1') {
            await setRecoveryMode(true);
        }
    }

    useEffect(() => {
        checkRecoveryMode();

        retriveDraftRaQuery.refetch();
        fetchPrefixesQuery.refetch();
        fetchCountriesQuery.refetch();

        // document.addEventListener("scroll", (event) => {
        //     // prova per Box
        //     if (window.scrollY > 68) {
        //         // console.log("--->>>", window.scrollY);
        //         createRaStepperRef.current.classList.add("stepperFixed");
        //         createRaContentRef.current.classList.add("patting-top-64");
        //     } else {
        //         createRaStepperRef.current.classList.remove("stepperFixed");
        //         createRaContentRef.current.classList.remove("patting-top-64");
        //     }
        //
        //     // // prova per appbar
        //     // if (window.scrollY > 64) {
        //     //     createRaStepperRef.current.classList.add("patting-top-64");
        //     //     // createRaContentRef.current.classList.add("patting-top-64");
        //     // } else {
        //     //     createRaStepperRef.current.classList.remove("patting-top-64");
        //     //     createRaContentRef.current.classList.remove("patting-top-64");
        //     // }
        // });

        //1200
    },[]);

    const stepHistory = useRef({ current: step as (typeof step | null), previous: null as (typeof step | null) });
    useEffect(() => {
      const stepHistoryValue = stepHistory.current;
      if (step !== stepHistoryValue.current) {
        stepHistoryValue.previous = stepHistoryValue.current;
        stepHistoryValue.current = step;
      }

      //console.log("step updated", step, { recoveryMode, keyLess })
      skipPreAuthStep();
    }, [step, recoveryMode, keyLess]);

    
    function skipPreAuthStep() {
      //console.log('---LB skipPreAuthStep ---');

      // Only for Standard RA
      if (keyLess) return

      if(recoveryMode) return

      // Calculate target step
      /*
      let preauthStep = -1
      if (recoveryMode) {
        preauthStep = 4;
      } else {
        preauthStep = 4;
      }
      */
      let preauthStep = 4;

      // Only run on PreAuth step
      if (step.active !== preauthStep) return;

      // Only when hasAnyTransaction is true
      if (!bookingDataValue.hasAnyTransaction) return;
  
      if (!bookingDataValue.checkPreAuth) return;

      // Only when preAuthAmount is set
      if (preAuthAmount == null) return;

      // === Calculate step skipping ===

      const stepHistoryValue = stepHistory.current;
      const completed = step.completed.concat();
      let nextStep: number

      // Going to next step
      if (stepHistoryValue.current.active > stepHistoryValue.previous.active) {
				completed[step.active] = true;
        nextStep = step.active + 1;
      }
      // Going to previous step
      else {
        const completed = step.completed.concat();
				completed[step.active] = false;
        nextStep = step.active - 1;
      }
      //console.log('LB preAuthAmount',preAuthAmount);
      raUpdateByCounterSaleMutation.mutateAsync({
        idRentalAgreement: bookingDataValue.idRentalAgreement,
        keyless: keyLess,
        preauthRentalAmount: preAuthAmount.firstPreAuth,
      } as any)
        .then(() => {
          setStep({
            active: nextStep,
            completed,
            numSteps: step.numSteps
          });
        })

        if (bookingDataValue.hasToBeReconciled === true) {
           businessContinuitySkipPaymentMutation.mutateAsync({idRentalAgreement: bookingDataValue.idRentalAgreement} as any);
        }
    
        if (!bookingDataValue.hasDispositiveToBeReconciled === true) {
           invoiceDraftMutation.mutateAsync({idRA: bookingDataValue.idRentalAgreement} as any);
        }

    }

    	/** Scatta Emissione della fattura su OK del pagamento (dispositive) */
    const invoiceDraftMutation = useMutation(
      raPreAuthPaymentInvoiceDraft,
      {
          onError: (error) => {
              dispatchGlobalData({
                  type: 'SET_SNACKBAR',
                  payload: {
                      snackbar: {
                          message: t("preAuth.message.error.generateInvoiceDraft"),
                          type: snackbarTypes.error,
                          show: true,
                          autoClose: true,
                      }
                  }
              });
          }
      });

          	/** Business continuity skip payment*/
	const businessContinuitySkipPaymentMutation = useMutation(
		fetchBusinessContinuitySkipPayment,
		{
			onError: (error) => {
				dispatchGlobalData({
					type: 'SET_SNACKBAR',
					payload: {
						snackbar: {
							message: t('translation:error.tryLater'),
							type: snackbarTypes.error,
							show: true,
							autoClose: true,
						}
					}
				});
			}
		});

  /** update ra by counter sale - /ra/update-by-counter-sale - required when PreAuth step is skipped */
	const raUpdateByCounterSaleMutation = useMutation(
		raUpdateByCounterSale,
		{
			onError: (error) => {
				dispatchGlobalData({
					type: 'SET_SNACKBAR',
					payload: {
						snackbar: {
							message: t('translation:error.tryLater'),
							type: snackbarTypes.error,
							show: true,
							autoClose: true,
						}
					}
				});
			}
		});

    return (
        <Box>
            <SectionHeader title={
                <Stack direction="row" alignItems="center" columnGap={3}>
                    <Stack direction="row" alignItems="end" columnGap={1}>
                        <Typography variant="bodyLarge">{t("createRa.header.title")}</Typography>
                        { queryParameters.get('recoveryMode') === '1' &&
                            <Chip label={t("createRa.header.chip.recovery-mode")} color={'error'} icon={<HealthAndSafety />}/>
                        }
                    </Stack>
                </Stack>
            } />

            <ContentBody>
                <Paper elevation={1} sx={{ p: theme.spacing(2), mb: theme.spacing(2) }}>
                    {/*<AppBar ref={createRaStepperRef} sx={{position: 'sticky', backgroundColor: '#fff', zIndex: (theme) => theme.zIndex.drawer-500}}>*/}
                    {/*    <>*/}
                    {/*    <Stepper activeStep={step.active} color={"accent"} sx={{mb: theme.spacing(3)}}>*/}
                    {/*        {steps*/}
                    {/*            .map((label: string, index: number) => (*/}
                    {/*                <Step key={label} completed={step.completed[index]}>*/}
                    {/*                    <StepButton color="accent" onClick={() => {handleStep(index)}}>*/}
                    {/*                        {t(label)}*/}
                    {/*                    </StepButton>*/}
                    {/*                </Step>*/}
                    {/*            ))}*/}
                    {/*    </Stepper>*/}
                    {/*    {*/}
                    {/*            step.active === 3 &&*/}
                    {/*        <Box sx={{mr: theme.spacing(2)}}>*/}
                    {/*            cost*/}
                    {/*        </Box>*/}
                    {/*    }*/}
                    {/*    </>*/}

                    {/*</AppBar>*/}

                    <Box id="createRaStepper" ref={createRaStepperRef} sx={{display: 'flex', justifyContent: 'space-between', zIndex: 1000}}>
                        <Stepper activeStep={step.active} color={"accent"} sx={{mb: theme.spacing(3)}}>
                        {steps
                                .map((label: string, index: number) => (
                                    <Step key={label} completed={step.completed[index]}>
                                        <StepButton color="accent" onClick={() => {handleStep(index)}}>
                                            {t(label)}
                                        </StepButton>
                                    </Step>
                                ))}
                        </Stepper>

                        {
                            step.active === 3 &&
                            <Box sx={{mx: theme.spacing(2)}}>
                                <AncillaryTotalCostInfo
                                    amount={counterSaleManagement?.last?.totalAmountVatIncluded}
                                    promoAmount={null}
                                    currency={counterSaleManagement?.last?.currency}
                                    fontWeight={"bodyLarge"}
                                />
                            </Box>
                        }
                    </Box>

                    <Box ref={createRaContentRef}>
                        {  step.active === 0 && <CustomerData keyLess={keyLess} keyLessInfo={keyLessInfo} /> }
                        {  step.active === 1 && <CheckDriversValidity /> }
                        {  !keyLess
                          ? <>
                            {  step.active === 2 && <OtpValidation /> }
                            {  step.active === 3 && <RaFilling keyLess={keyLess} keyLessInfo={keyLessInfo} /> }
                            {
                                recoveryMode && step.active === 4 &&
                                <ConsentMgmt />
                            }
                            {
                                (recoveryMode && step.active === 5 || !recoveryMode && step.active === 4) &&
                                <PreAuth keyLess={keyLess} />
                            }
                            {
                                (recoveryMode && step.active === 6 || !recoveryMode && step.active === 5) &&
                                <PreviewAndSigning keyLess={keyLess} keyLessInfo={keyLessInfo} />
                            }
                          </>
                          : <>
                            {  step.active === 2 && <RaFilling keyLess={keyLess} keyLessInfo={keyLessInfo} /> }
                            {
                                recoveryMode && step.active === 3 &&
                                <ConsentMgmt />
                            }
                            {
                                (recoveryMode && step.active === 4 || !recoveryMode && step.active === 3) &&
                                <PreAuth keyLess={keyLess} />
                            }
                            {
                                (recoveryMode && step.active === 5 || !recoveryMode && step.active === 4) &&
                                <PreviewAndSigning keyLess={keyLess} keyLessInfo={keyLessInfo} />
                            }
                          </>
                        }
                    </Box>

                </Paper>

            </ContentBody>

            <CustomLoader loading={retriveDraftRaQuery.isFetching || fetchCountriesQuery.isFetching || verifySignRaMutation.isLoading ||
                                    fetchCreateRaPricingAncillaryInfoMutation.isLoading || checkTransactionRaMutation.isLoading || raUpdateByCounterSaleMutation.isLoading} />

            <RecoveryDialogReason onSave={handleSaveReasonSave}
                                  open={reasonRecoveryDialog}
                                  dialogTitle={'recoveryRaReason.header.title'}
                                  backdropClick={false}>
            </RecoveryDialogReason>
            <NotAllowedDialog open={notAllowedDialogProps.open} title={notAllowedDialogProps.title} message={notAllowedDialogProps.message} buttonLabel={notAllowedDialogProps.buttonLabel} handleClose={notAllowedDialogProps.handleClose}/>
        </Box>
    );
}

export default CreateRa;